import { Component, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import OrganizationService from 'src/app/shared/services/organization.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { EnvironmentLoaderService } from '../../../../shared/services/environment-loader.service';
import { DownloadReportDialogComponent } from '../download-report-dialog/download-report-dialog.component';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { HttpErrorResponse } from '@angular/common/http';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { CreateDepartmentDialogData, Department, DepartmentUpdated } from 'src/app/features/department/department.model';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { IdTagTypeEnum } from 'src/app/shared/enums/id-tag-type.enum';
import { OrganizationDisplay } from '../../organization-models';
import { ChargingStationLocationCreateEditModalComponent } from 'src/app/features/charging-station-location/charging-station-location-create-modal/charging-station-location-create-edit-modal';
import { CreateDepartmentDialogComponent } from 'src/app/features/department/components/create-update-department-dialog/create-update-department-dialog.component';
import { LoadingSpinnerComponent } from '../../../../core/loading-spinner/loading-spinner.component';
import { RouterLink } from '@angular/router';
import { RequiresFeatureRoleDirective } from '../../../../shared/directives/requires-feature-role.directive';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ChargingStationLocationDialogData } from 'src/app/features/charging-station-location/charging-station-location-create-modal/charging-station-location-created-edit-dialog.service';

@Component({
  selector: 'adm-organization-details',
  templateUrl: './organization-details.component.html',
  styleUrl: './organization-details.component.scss',
  imports: [
    LoadingSpinnerComponent,
    RouterLink,
    RequiresFeatureRoleDirective,
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
  ],
})
export class OrganizationDetailsComponent {
  private organizationService = inject(OrganizationService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private departmentService = inject(DepartmentService);

  organization = input.required<OrganizationDisplay>();
  highlightCreatedDepartmentId = input<number | null>();
  isCustomerEntranceDialog = input<boolean>(false);
  departmentFilter = input<string>('');

  public sort = viewChild.required<MatSort>('sort');
  public departmentList = viewChild<MatTable<Department>>('departmentList');

  public displayColumns = [
    'departmentName',
    'departmentGroup',
    'departmentLocationCount',
    'idTagCount',
    'accountNumber',
    'agreementNumber',
    'actions',
  ];

  departmentDeleted = output<OrganizationDisplay>();
  linkNavigated = output<void>();
  public loading = signal<boolean>(true);
  public roles = this.environmentLoaderService.virtualRoles;
  public idTagStatusEnum: typeof IdTagStatusEnum = IdTagStatusEnum;
  public idTagTypeEnum: typeof IdTagTypeEnum = IdTagTypeEnum;
  private refresh$ = new Subject<void>();
  public highlightUpdatedDepartmentId$ = new BehaviorSubject<number | undefined>(undefined);
  public g4Info = 'G4 Kontonr til for afdelingen.';
  public okAftaleNr =
    'Det aftalenr., der er oprettet til lokationen i G4 og som også bruges i projekt id. Aftalenummeret anvendes til evt. forbrugsopkrævning, strømgodtgørelse og evt. elafgiftrefusion.';

  constructor() {
    const sort$ = toObservable(this.sort).pipe(
      filter((sort): sort is MatSort => sort !== undefined),
      distinctUntilChanged()
    );

    combineLatest([
      toObservable(this.organization).pipe(distinctUntilChanged()),
      sort$,
      toObservable(this.highlightCreatedDepartmentId).pipe(distinctUntilChanged()),
      this.refresh$.pipe(startWith(void 0)),
    ])
      .pipe(
        switchMap(([organization, sort, highlightDepartmentId]) =>
          this.organizationService.getDepartmentsForOrganization(organization.id, sort).pipe(
            map(departments => {
              const mapped = departments.map(d => {
                const highlight: BehaviorSubject<boolean> | undefined =
                  highlightDepartmentId === d.departmentId || this.highlightUpdatedDepartmentId$.value === d.departmentId
                    ? new BehaviorSubject<boolean>(true)
                    : undefined;
                setTimeout(() => {
                  highlight?.next(false);
                  this.highlightUpdatedDepartmentId$?.next(undefined);
                }, 3000);
                return {
                  ...d,
                  departmentName: d.departmentName + (d.isHeadquarter ? ' (Hovedkontor)' : ''),
                  highlight: highlight,
                };
              });
              this.departmentList()!.dataSource = new MatTableDataSource<Department>(mapped);
              this.applyFilter();
            })
          )
        ),
        catchError(err => {
          this.snackbarService.error(`Organisationens detaljer kunne ikke hentes.`);
          return of();
        }),
        tap(() => this.loading.set(false)),
        takeUntilDestroyed()
      )
      .subscribe();

    effect(() => {
      this.departmentFilter();
      this.applyFilter();
    });
  }

  refreshDetails() {
    this.refresh$.next();
  }

  private applyFilter() {
    const dataSource = this.departmentList()?.dataSource;
    if (dataSource instanceof MatTableDataSource) {
      dataSource.filter = this.departmentFilter().trim().toLowerCase();
    }
  }

  onDepartmentClick(department: Department) {
    console.warn('Department clicked:', department.departmentName);
  }

  onDepartmentLinkNavigate() {
    if (this.isCustomerEntranceDialog()) {
      this.linkNavigated.emit();
    }
  }

  openCreateLocationDialog(departmentId: number) {
    const dialogRef = this.dialog.open(ChargingStationLocationCreateEditModalComponent, {
      width: '650px',
      data: {
        departmentId: departmentId,
        organizationId: this.organization().id,
      } as ChargingStationLocationDialogData,
    });
    dialogRef.afterClosed().subscribe(() => {
      this.refresh$.next();
    });
  }

  public openUpdateDepartmentDialog(id: number) {
    const dialogRef = this.dialog.open(CreateDepartmentDialogComponent, {
      data: <CreateDepartmentDialogData>{
        departmentId: id,
        organizationId: this.organization().id,
        organizationName: this.organization().name,
        organizationType: this.organization().organizationType,
      },
    });

    dialogRef.afterClosed().subscribe((departmentUpdated: DepartmentUpdated) => {
      if (departmentUpdated) {
        this.highlightUpdatedDepartmentId$.next(departmentUpdated.departmentId);
        departmentUpdated.organizationId = this.organization().id;
        this.refresh$.next();
      }
    });
  }

  public openDownloadReportDialog(departmentId: number, departmentGroupName: string) {
    this.dialog.open(DownloadReportDialogComponent, {
      width: '600px',
      data: {
        organizationId: this.organization().id,
        organizationName: this.organization().name,
        departmentId: departmentId,
        departmentGroupName: departmentGroupName,
      },
    });
  }

  public openDeleteDepartmentDialog(id: number, name: string) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Slet afdeling',
        text: `Er du sikker på at du vil slette afdelingen ${name}?`,
        confirmText: 'Slet',
        denyText: 'Annuller',
        icon: { name: 'trash-s' },
      },
      disableClose: true,
    });
    confirmDialogRef.afterClosed().subscribe(async confirmed => {
      if (confirmed) {
        this.departmentService
          .deleteDepartment(id)
          .pipe(
            catchError((httpError: HttpErrorResponse) => {
              if (httpError.status == 404 && httpError.error) {
                const error = httpError.error as ErrorMessage;
                if (error.code === 300005) {
                  this.snackbarService.warning('Afdeling kunne ikke findes.');
                }
              } else if (httpError.status == 400 && httpError.error) {
                const error = httpError.error as ErrorMessage;
                if (error.code === 100159) {
                  this.snackbarService.warning('Afdeling kunne ikke slettes da den har lokationer.');
                } else if (error.code === 100167) {
                  this.snackbarService.warning('Afdeling kunne ikke slettes da den har aktive brikker tilknyttet.');
                } else if (error.code === 100168) {
                  this.snackbarService.warning('Afdeling kunne ikke slettes da den har bruger(e) med aktive brikker tilknyttet.');
                }
              } else {
                this.snackbarService.error('Der opstod en ukendt fejl under sletning af afdeling.');
              }
              return of();
            })
          )
          .subscribe(() => {
            this.snackbarService.success('Afdeling blev slettet!');
            this.departmentDeleted.emit(this.organization());
            this.refresh$.next();
          });
      }
    });
  }
}
